import React from 'react';
import { inWorkingOrder } from '../../utils/stopOrder';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { Calendar, Clock, ChevronRight, CheckCircle, Wallet } from 'lucide-react';
import CrewDayTimer from './CrewDayTimer';
import JudgedOnThis from '../../components/crew/JudgedOnThis';
import DayBriefCard from '../../components/DayBriefCard';
import ShopWeeklyCard from '../../components/ShopWeeklyCard';
import { useReadOnly } from '../../hooks/useReadOnly';
import { businessToday, isoPlusDays } from '../../utils/time';
import { placeLabel } from '../../utils/format';
import WorkLine from '../../components/crew/WorkLine';

function todayISO() { return businessToday(); }

function fmtTime(t: string) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = Number(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${m} ${ampm}`;
}

function fmtDate(iso: string) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

export default function CrewDashboard() {
  const { user } = useAuth();
  const readOnly = useReadOnly();
  const today = todayISO();

  // All upcoming jobs (today's list and the week ahead)
  const { data: allJobsData } = useQuery(['crew-all-jobs'], () =>
    api.get('/jobs').then(r => r.data.jobs as any[])
  );
  // The route they are on, if started: the same query the day timer keeps, so
  // the two never disagree. Once a route is running the morning note and the
  // "judged on" list fold away and the page is the timer and the stops
  // (owner, Nov 4 2026: "once they start the route it hides the morning list
  // and the you-are-judged-on-this").
  const { data: session } = useQuery(
    ['route-session-active'],
    () => api.get('/route-sessions/active', { params: { date: today } }).then(r => r.data),
    { refetchInterval: 15_000 }
  );
  const onRoute = !!session?.active;

  const allJobs = allJobsData || [];
  const todayJobs = inWorkingOrder(allJobs.filter(j => j.scheduled_date === today && j.status !== 'skipped'));

  const in7ISO = isoPlusDays(today, 7);
  const upcomingJobs = inWorkingOrder(allJobs
    .filter(j => j.scheduled_date > today && j.scheduled_date <= in7ISO && j.status !== 'skipped'))
    .slice(0, 5);

  return (
    <div className="space-y-5">

      {/* ── Before the route starts: the morning note, the shop weekly (for whoever wears the Mechanic tag — the
          card decides), and what every yard is judged on. All three fold away once the route is running. ── */}
      {!onRoute && (
        <>
          <DayBriefCard />
          <ShopWeeklyCard />
          <JudgedOnThis />
        </>
      )}

      {/* ── Day Timer ── */}
      {/* Every control in the timer writes (start a day, start a stop, break,
          finish), and a view-only login has all of them refused server-side —
          including the automatic geofence start. Show what it is instead. */}
      {readOnly
        ? <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-800">
            Day timer is off on this view-only login. The schedule below is exactly what the crew sees.
          </div>
        : <CrewDayTimer />}

      {/* Greeting */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">
          Hey, {user?.name?.split(' ')[0] ?? 'there'} 👋
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">{fmtDate(today)}</p>
      </div>

      {/* Money and the score live on their own page (owner, Nov 4 2026). */}
      <Link to="/crew/pay" className="flex items-center gap-2 bg-white rounded-xl shadow-sm px-4 py-3 border border-gray-100 text-sm font-semibold text-gray-800">
        <Wallet className="h-4 w-4 text-green-600" /> Pay &amp; score <ChevronRight className="h-4 w-4 text-gray-300 ml-auto" />
      </Link>

      {/* Today's jobs */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Today's Jobs</h2>
          <Link to="/crew/jobs" className="text-xs text-brand-600 font-medium hover:text-brand-700">
            Full schedule →
          </Link>
        </div>
        {todayJobs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-4 text-center">
            <Calendar className="h-6 w-6 text-gray-300 mx-auto mb-1" />
            <p className="text-sm text-gray-500">No jobs today.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {todayJobs.map(job => {
              const label = placeLabel(job.property_label) || job.address?.split(',')[0] || job.customer_name || job.title;
              const isDone = job.status === 'complete';
              return (
                <Link key={job.id} to={`/crew/jobs/${job.id}`}
                  className="flex items-center gap-3 bg-white rounded-xl shadow-sm p-3 hover:shadow-md transition-shadow">
                  <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${isDone ? 'bg-green-400' : 'bg-brand-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm break-words leading-snug">{label}</p>
                    <WorkLine job={job} className="mt-0.5" />
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {job.time_start && (
                        job.time_is_firm
                          ? <span className="flex items-center gap-0.5 text-xs text-white bg-red-600 font-bold px-2 py-0.5 rounded-full">
                              <Clock className="h-3 w-3" /> START {fmtTime(job.time_start)}
                            </span>
                          : <span className="flex items-center gap-0.5 text-xs text-gray-500">
                              <Clock className="h-3 w-3" /> ETA {fmtTime(job.time_start)}
                            </span>
                      )}
                      {job.estimated_minutes_snapshot && (
                        <span className="text-xs text-gray-500">{job.estimated_minutes_snapshot}min</span>
                      )}
                      {isDone && <span className="flex items-center gap-0.5 text-xs text-green-600"><CheckCircle className="h-3 w-3" /> Done</span>}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Upcoming preview */}
      {upcomingJobs.length > 0 && (
        <div>
          <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">Coming Up</h2>
          <div className="space-y-2">
            {upcomingJobs.map(job => {
              const label = placeLabel(job.property_label) || job.address?.split(',')[0] || job.customer_name || job.title;
              return (
                <Link key={job.id} to={`/crew/jobs/${job.id}`}
                  className="flex items-center gap-3 bg-white rounded-xl shadow-sm p-3 hover:shadow-md transition-shadow">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm break-words leading-snug">{label}</p>
                    <WorkLine job={job} className="mt-0.5" />
                    <p className="text-xs text-gray-500 mt-0.5">
                      {fmtDate(job.scheduled_date)}
                      {job.time_start ? (job.time_is_firm
                        ? <span className="text-red-600 font-bold"> · ⏰ START {fmtTime(job.time_start)}</span>
                        : ` · ETA ${fmtTime(job.time_start)}`) : ''}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}
